import React, {
  ChangeEvent,
  Fragment,
  MutableRefObject,
  ReactNode,
  Ref,
  RefObject,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';
import { classNames } from '../../utils';
import { ChangeEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';
import { FieldError, get, useForm, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { ReactComponent as CloseIcon } from '../icons/close.svg';
import { DropdownOptions, DropdownSelectInput } from './dropdown-select-input';
import { Combobox, Transition } from '@headlessui/react';
import { Maybe } from 'graphql/jsutils/Maybe';

type SelectOption = {
  label: string;
  value: string | number;
  disabled?: boolean;
};
interface TextSearchInputProps {
  type?: React.HTMLInputTypeAttribute;
  name?: string;
  placeholder?: string;
  focusPlaceholder?: string;
  disabled?: boolean;
  value?: Maybe<string> | undefined | null;
  onChange?: (value: any) => void;
  onBlur?: (value: any) => void;
  onFocus?: (value: any) => void;
  onSubmit?: (value: number | string | null) => void;
  onKeyDown?: (value: any) => void;
  onClear?: () => void;
  className?: string;
  inputClassName?: string;
  tabIndex?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  fieldErrorOverride?: FieldError;
  autoFocus?: boolean;
  submitOnEnter?: boolean;
  defaultValue?: string | null | undefined;
  options: SelectOption[];
  customErrorMessage?: string | ReactNode;
  noOptionsText?: string;
  inputWrapperClassName?: string;
  dropdownContainerClassName?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  showClearable?: boolean;
}

// eslint-disable-next-line
export const TextSearchInput: React.FC<TextSearchInputProps> = (props) => {
  const {
    className = '',
    type,
    name,
    disabled,
    value,
    onBlur,
    onFocus,
    onChange,
    onSubmit,
    onKeyDown,
    placeholder,
    focusPlaceholder,
    inputClassName = '',
    maxLength,
    fieldErrorOverride,
    autoFocus,
    submitOnEnter,
    defaultValue,
    options,
    onClear,
    customErrorMessage,
    noOptionsText = 'No options found.',
    inputWrapperClassName,
    dropdownContainerClassName,
    prefix,
    suffix,
    showClearable = true,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  const defaultOption = options.find((o) => o.value === defaultValue);
  const [selectedValue, setSelectedValue] = useState<
    SelectOption | null | string
  >(defaultOption ? defaultOption : null);
  const [placeHolderString, setPlaceHolderString] = useState(placeholder);
  const [query, setQuery] = useState(defaultValue ? defaultValue : '');
  const methods = useFormContext();

  let errors = methods?.formState.errors;

  if (name && fieldErrorOverride) {
    errors = { [name]: fieldErrorOverride };
  }

  const error =
    fieldErrorOverride || (methods && name ? get(errors, name) : null);

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) => {
          if (option.label) {
            return option?.label
              .toLowerCase()
              .replace(/\s+/g, '')
              .includes(query.toLowerCase().replace(/\s+/g, ''));
          }
        });

  const onSelect = (option: SelectOption | null) => {
    console.log('COMBO BOX value =>', option);
    if (option?.disabled) return;
    setSelectedValue(option);

    if (onChange && option) {
      onChange(option);
    }
  };
  return (
    <>
      <Combobox value={selectedValue} onChange={onSelect}>
        <div className="flex flex-col items-center w-full">
          <div
            className={twMerge(
              'relative mt-1 w-full',
              inputWrapperClassName ? inputWrapperClassName : ''
            )}
          >
            <div
              className={twMerge(
                'relative rounded-[4px] border-[1px]',
                disabled ? 'bg-sidewalk-30 cursor-default' : 'bg-white',
                error
                  ? 'border-hydrant-500 bg-hydrant-30'
                  : 'border-sidewalk-200',
                selectedValue ? 'pl-3 pr-8' : 'px-[12px]',
                className
              )}
            >
              <Combobox.Input
                displayValue={(option) => {
                  //@ts-ignore
                  return selectedValue ? selectedValue.label : query;
                }}
                ref={inputRef}
                type={type}
                name={name}
                onChange={(e) => {
                  if (name) {
                    methods.setValue(name, e.target.value);
                  }
                  setQuery(e.target.value);
                  if (onKeyDown) {
                    onKeyDown(e.target.value);
                  }
                }}
                //@ts-ignore
                onBlur={(e) => {
                  if (placeholder) {
                    setPlaceHolderString(placeholder);
                  }

                  if (onBlur) {
                    onBlur(e.target.value);
                  }
                }}
                className={twMerge(
                  'typo-body text-sidewalk-900 block h-[44px] w-full border-none p-0 outline-none ring-0 placeholder:text-sidewalk-600 focus:border-none focus:shadow-none focus:ring-0',
                  disabled ? 'bg-sidewalk-30' : '',
                  error ? 'bg-hydrant-30' : '',
                  inputClassName
                )}
                placeholder={placeHolderString}
                onFocus={(e) => {
                  if (onFocus) {
                    onFocus(e.target.value);
                  }
                }}
                onClick={() => {
                  if (focusPlaceholder) {
                    setPlaceHolderString(focusPlaceholder);
                  }
                  handleFocus();
                }}
              />
              <>{prefix ? prefix : <></>}</>
              <>{suffix ? suffix : <></>}</>
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                {selectedValue ? (
                  <div
                    className="pointer-events-auto cursor-pointer absolute inset-y-0 right-0 mt-[-2px] flex items-center pr-3"
                    onClick={() => {
                      console.log('cleared!');
                      setSelectedValue(null);
                      setQuery('');

                      if (onClear) {
                        onClear();
                      }
                    }}
                  >
                    {showClearable ? (
                      <CloseIcon className="text-sidewalk-600" />
                    ) : (
                      <></>
                    )}
                  </div>
                ) : null}
              </Combobox.Button>
            </div>
            {query !== '' ? (
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => {
                  if (filteredOptions.length === 1) {
                    onSelect(filteredOptions[0]);
                  }
                }}
              >
                <Combobox.Options
                  className={classNames(
                    'z-[10000] absolute mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm rounded-[4px]',
                    dropdownContainerClassName
                  )}
                >
                  {filteredOptions.length === 0 && query !== '' ? (
                    <div className="relative cursor-default select-none py-2 px-[12px] mx-2 typo-body-small text-hydrant-500">
                      {noOptionsText}
                    </div>
                  ) : (
                    filteredOptions.map((option) => (
                      <Combobox.Option
                        key={option.label}
                        className={({ active, selected }) =>
                          twMerge(
                            'relative cursor-pointer select-none py-2 px-[12px] mx-2 rounded-[4px] text-sidewalk-900 hover:bg-hardwood-50',
                            active ? 'bg-hardwood-100' : '',
                            selected ? 'bg-hardwood-50' : 'bg-white',
                            option.disabled
                              ? 'pointer-events-none cursor-not-allowed text-sidewalk-300 hover:bg-white'
                              : ''
                          )
                        }
                        value={option}
                      >
                        {({ selected, active }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? 'font-medium' : 'font-normal'
                              }`}
                            >
                              {option.label}
                            </span>
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            ) : null}
          </div>
          {name && error && customErrorMessage ? customErrorMessage : null}
          {name && error && !customErrorMessage ? (
            <ErrorMessage
              errors={errors}
              name={name}
              render={({ message }) => {
                return (
                  <div className="typo-body-small text-hydrant-500 self-start">
                    {message}
                  </div>
                );
              }}
            />
          ) : null}
        </div>
      </Combobox>
    </>
  );
};
